import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import { GetFunctionsByEventId, GetGuestsByEventId, GetCardTypes } from '../../api/api';
import { isMarriageEventType, fmtDate, fmtTime } from '../../utils/helpers';
import { functionHeadcount } from '../../utils/headcount';

// Shared logic for the venue headcount: persons per function, parties deduped
// by mobile (a family invited by several members counts once), split by Side,
// plus the unnamed guests on extra cards for the Reception-card functions
// (utils/headcount.js holds the rule).
export function useHeadcount(event) {
  const { isAdmin } = useAuth();
  const nav = useNavigate();
  const openRow = (fn) => nav('/app/members?fn=' + fn.FunctionId); // drill in to that function's guests
  const toast = useToast();
  const isTwoSided = isMarriageEventType(event?.EventTypeName || event?.EventType);
  const [functions, setFunctions] = useState([]);
  const [rows, setRows] = useState([]);
  const [cardTypes, setCardTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      GetFunctionsByEventId({ EventId: event.EventId }),
      GetGuestsByEventId({ EventId: event.EventId }),
      GetCardTypes({ EventId: event.EventId }).catch(() => []), // table not migrated yet → no extras
    ]).then(([f, g, t]) => {
      setFunctions(Array.isArray(f) ? f : []);
      setRows(Array.isArray(g) ? g : []);
      setCardTypes(Array.isArray(t) ? t : []);
    }).catch(() => toast('Failed to load')).finally(() => setLoading(false));
  }, [event, toast]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const perFunction = useMemo(
    () => functions.map(fn => ({ fn, ...functionHeadcount(rows, fn.FunctionId, cardTypes) })),
    [functions, rows, cardTypes]
  );

  const grand = useMemo(
    () => perFunction.reduce((a, r) => ({ parties: a.parties + r.parties, persons: a.persons + r.persons }), { parties: 0, persons: 0 }),
    [perFunction]
  );

  // Share / Export: the per-function table as it is shown, with the totals
  // line the caterer actually reads.
  const report = useMemo(() => ({
    title: 'Headcount',
    columns: [
      { key: 'FunctionName', label: 'Function', exportValue: (r) => r.fn.FunctionName },
      { key: 'FunctionDate', label: 'Date', exportValue: (r) => fmtDate(r.fn.FunctionDate) },
      { key: 'StartTime', label: 'Time', exportValue: (r) => fmtTime(r.fn.StartTime) },
      { key: 'Venue', label: 'Venue', exportValue: (r) => r.fn.Venue || '' },
      { key: 'parties', label: 'Cards / parties', align: 'num' },
      ...(isTwoSided ? [
        { key: 'groom', label: 'Groom', align: 'num', exportValue: (r) => r.groom ?? '' },
        { key: 'bride', label: 'Bride', align: 'num', exportValue: (r) => r.bride ?? '' },
      ] : []),
      { key: 'persons', label: 'Persons', align: 'num' },
    ],
    rows: perFunction,
    totalsRow: { FunctionName: 'Total', parties: grand.parties, persons: grand.persons },
  }), [perFunction, grand, isTwoSided]);

  return { isAdmin, loading, perFunction, grand, isTwoSided, openRow, report };
}
